// Фича Steam Spin на экране, см. GDD, раздел 5.
//
// Она собирает четыре части: створки мини-слота, манометры, значения множителей на поле
// и взгляд игры. Раунд знает о ней только через RoundFeature: после показа итога спина
// и в конце раунда он даёт ей показать своё и ждёт, пока она кончит.
//
// Стадия 1. Wild на барабане открывает створку под своим номером, и едет она тем же спином,
// на котором wild выпал: игрок видит выигрыш, а сразу за ним — что этот wild открыл. Разом
// со створкой манометр этого барабана идёт на единицу, и та же единица встаёт множителем
// в открывшемся окне.
//
// Стадия 2. Она включается в конце раунда, когда открыты все три створки. Игра объявляет её
// заглушкой поля с надписью Steam Charging, как объявляет nudge respin, см. GDD, раздел 5.2
// и попап id_0 в разделе 7.2. Манометры к этому мигу уже стоят на своей единице: каждый
// поднялся вместе со своим барабаном.
//
// Дальше wild multiplier заряжает манометр своего барабана: стрелка идёт делениями вверх,
// а множитель в окне мини-слота растёт вместе с ней.
//
// Бонус. Манометр, дошедший до десятки, запускает его в конце раунда, см. GDD, раздел 5.2.
// Идёт бонус так:
//
// 1. Поле закрывается надписью Steam Spin Activated, а на нижней створке всплывает кнопка.
//    Игру она не держит: не дождавшись игрока, она жмётся сама, но ждёт дольше прочих кнопок
//    игры — это единственное место бонуса, где игрок решает сам. Разом с надписью паровоз
//    трогается со станции и идёт весь бонус, см. ui/ride.ts и ADR-51.
// 2. Манометры доходят до своего заряда, а окна мини-слота — до своих множителей. Steam Spin
//    здесь стоит: его манометры зарядили спины раунда, и двигать нечего.
// 3. Взгляд подходит к мини-слоту. Множители в его окнах ещё стоят: игрок видит, на что
//    множится выигрыш.
// 4. Множители уходят, барабаны крутятся и встают на итог бонуса. Выигравшие символы играют
//    свои анимации, см. GDD, раздел 5.3.
// 5. Взгляд возвращается к обычному виду.
// 6. На закрытом поле набегает счётчик выигрыша, а за ним встаёт поздравление с кнопкой,
//    см. GDD, раздел 7.2, попап id_4. Разом со счётчиком паровоз подходит к станции.
// 7. Стрелки возвращаются в ноль, створки мини-слота закрываются, поле открывается,
//    и фича начинает со стадии 1, см. GDD, раздел 5.4.
//
// Red Hot Spin. Три скаттера на экране запускают тот же бонус, см. GDD, раздел 6. Идёт он
// теми же шагами: разница в надписи на закрытом поле и в том, что шагом 2 стрелки уходят
// на потолок разом, счётчик доходит до трёх десяток, а закрытые окна мини-слота открываются
// на своих десятках, см. GDD, раздел 6.2.
//
// Своего показа у фичи нет и не будет: бонус один, а запуска у него два. Что именно запустило
// этот бонус, говорит бэкенд — скаттеры клиент не считает.

import type { Container } from 'pixi.js';

import { boardOf, type GameConfig, type SteamSpinConfig } from '../../api/gameConfig';
import type { SessionState, SteamSpinState } from '../../api/session';
import type { BonusSpin, BonusTrigger, OpenedReel, RoundResult, SpinResult, SteamSpinSpin } from '../../api/spin';
import { REELS_GRID, TOTAL_METER_PLACE } from '../../assets/catalog';
import type { AssetManager } from '../../assets/manager';
import type { RoundFeature } from '../../game/spinRound';
import type { SymbolLibrary } from '../../game/symbols';
import { text } from '../../i18n/text';
import { music, sfx } from '../../sound/sound';
import { moneyText } from '../../ui/amount';
import type { Camera } from '../../ui/camera';
import type { FieldCover } from '../../ui/fieldCover';
import type { Ride } from '../../ui/ride';
import type { Steam } from '../../ui/steam';

import { BonusReels } from './bonusReels';
import { Gauges } from './gauges';
import { MultiplierSymbols, type MarkedField } from './multipliers';
import { TotalMultiplier } from './totalMultiplier';

/**
 * Поздравление над выигранной суммой, попапы id_4 и id_5 в GDD, раздел 7.2.
 *
 * Надпись на обоих одна: у Steam Spin и Red Hot Spin поздравление не различается ничем,
 * кроме номера попапа. Строк в ней две, и в паке перевода каждая названа своим ключом.
 */
function congratulation(): string {
  return `${text('congratulation')}\n${text('youWon')}`;
}

/**
 * Насколько взгляд подходит к мини-слоту на время бонуса.
 *
 * При обычном виде на экран вписана полоса от бонусного ряда до низа поля. Приближение
 * в полтора раза оставляет на ней бонусный ряд, манометры и верх машины: как раз то,
 * на что игрок смотрит весь бонус, см. GDD, раздел 5.3.
 */
const BONUS_ZOOM: number = 1.4;

/**
 * Сколько кнопка спина стоит на створке перед прокруткой бонусных барабанов.
 *
 * Это единственное место бонуса, где игра ждёт игрока: дальше она играет сама. Двух секунд
 * ему мало — он читает надпись на створках, видит под ней кнопку и только потом решает,
 * жать ли её. Дольше четырёх кнопка стояла бы уже впустую.
 */
const SPIN_BUTTON_SECONDS: number = 3.5;

/**
 * Номера бонусных барабанов, открытых спином.
 *
 * Бэкенд шлёт вместе с номером позицию полосы, а показу она не нужна: открытое окно
 * показывает не символ полосы, а значение множителя.
 */
export function openedReels(spin: SteamSpinSpin): number[] {
  return spin.opened.map((opened: OpenedReel) => opened.reel);
}

/**
 * Значения множителей в окнах мини-слота.
 *
 * Окно показывает заряд своего манометра, см. GDD, раздел 5.2. На стадии 1 манометры стоят
 * в нуле, а нулевого множителя не бывает: там во всех окнах стоит x1.
 */
export function multiplierValues(gauges: readonly number[]): number[] {
  return gauges.map((gauge: number) => Math.max(1, gauge));
}

/**
 * Общий множитель: сумма зарядов всех манометров, см. GDD, разделы 5.2 и 5.3.
 *
 * Его показывает счётчик справа от мини-слота, и на него же бонус умножает свой выигрыш.
 */
export function totalValue(gauges: readonly number[]): number {
  return gauges.reduce((total: number, gauge: number) => total + gauge, 0);
}

/**
 * Надпись, которой бонус объявляется на закрытом поле.
 *
 * Бонус один, а запуска у него два, и попап у каждого свой: id_1 у Steam Spin, id_2
 * у Red Hot Spin, см. GDD, раздел 7.2.
 */
export function bonusCaption(trigger: BonusTrigger): string {
  return text(trigger === 'redHotSpin' ? 'redHotSpin' : 'steamSpin');
}

/** Всё, из чего фича собирается. */
export interface SteamSpinParts {
  /** Описание игры вместе с фичей и доской мини-слота. */
  readonly config: GameConfig;

  /** Состояние фичи из сессии: с него игра начинает показ. */
  readonly state: SteamSpinState;

  readonly symbols: SymbolLibrary;
  readonly assets: AssetManager;

  /** Барабаны поля: у них множители берут положение помеченных ячеек. */
  readonly field: MarkedField;

  /** Заглушка поля. Ею фича объявляет стадию 2 и ведёт весь бонус. */
  readonly cover: FieldCover;

  /** Взгляд игры. Им фича подводит игрока к мини-слоту на время бонуса. */
  readonly camera: Camera;

  /** Поездка. Ею фича трогает поезд со станции на весь бонус и приводит обратно. */
  readonly ride: Ride;

  /** Пар у манометров. Им фича бьёт из крана на открытой створке и на начале бонуса. */
  readonly steam: Steam;
}

/** Фича Steam Spin: створки мини-слота, манометры и значения множителей. */
export class SteamSpin implements RoundFeature {
  readonly #bonus: BonusReels;
  readonly #gauges: Gauges;
  readonly #total: TotalMultiplier;
  readonly #multipliers: MultiplierSymbols;
  readonly #cover: FieldCover;
  readonly #camera: Camera;
  readonly #ride: Ride;
  readonly #steam: Steam;

  /** Состояние фичи на этот миг. Раунд двигает его своим итогом. */
  #state: SteamSpinState;

  /** Оборвал ли игрок показ. Оборванный показ дальше не идёт, а сразу сворачивается. */
  #rushed = false;

  /** @throws Error если фичи в описании игры нет */
  constructor(parts: SteamSpinParts) {
    const { config, state, symbols, assets, field, cover, camera, ride, steam } = parts;

    const feature: SteamSpinConfig | undefined = config.steamSpin;
    if (feature === undefined)
      throw new Error('В описании игры нет фичи Steam Spin');

    const board: GameConfig = boardOf(config, feature.bonusBoard);

    this.#state = state;
    this.#cover = cover;
    this.#camera = camera;
    this.#ride = ride;
    this.#steam = steam;

    this.#bonus = new BonusReels(board, symbols, assets, feature.multiplierSymbol);
    this.#gauges = new Gauges(assets, board.reels.length, feature.gaugeMax);
    this.#total = new TotalMultiplier(assets, TOTAL_METER_PLACE.centerX, TOTAL_METER_PLACE.centerY);
    this.#multipliers = new MultiplierSymbols(symbols, field, feature.multiplierSymbol);

    this.#show(state);
  }

  /** Мини-слот со створками. Он лежит в бонусном ряду над полем. */
  get bonusView(): Container {
    return this.#bonus.view;
  }

  /** Манометры. Они лежат на рамке между бонусным рядом и полем. */
  get gaugesView(): Container {
    return this.#gauges.view;
  }

  /** Счётчик общего множителя. Он стоит справа от мини-слота. */
  get totalView(): Container {
    return this.#total.view;
  }

  /** Символы Wild Multiplier со своими значениями. Они лежат поверх символов поля. */
  get multipliersView(): Container {
    return this.#multipliers.view;
  }

  /** Состояние фичи на этот миг. */
  get state(): SteamSpinState {
    return this.#state;
  }

  /**
   * Показывает, что этот спин сделал: открыл бонусные барабаны и зарядил манометры.
   *
   * Всё идёт разом: створка едет, стрелка своего манометра поворачивается, а множитель
   * в окне появляется. Это один и тот же ход — на стадии 1 его делает выпавший wild,
   * на стадии 2 wild multiplier. Ждать друг друга барабанам незачем: один спин может
   * тронуть сразу три.
   *
   * Показ идёт по спинам, а не по раунду: игрок видит, что оно открыло и на сколько зарядило,
   * тут же, а не в конце цепочки респинов. Идёт он разом с показом выигрыша на поле:
   * это разные места экрана, см. game/spinRound.ts.
   */
  async afterSpin(spin: SpinResult): Promise<void> {
    this.#rushed = false;

    const played: SteamSpinSpin | undefined = spin.steamSpin;
    if (played === undefined)
      return;

    const reels: number[] = openedReels(played);

    // Створку мини-слота открывает пар: из крана машины бьёт струя, см. ui/steam.ts.
    // Один спин может открыть три створки разом, и струя на них одна: три струи подряд
    // на одном месте слились бы в одно облако.
    if (reels.length > 0)
      this.#steam.burst();

    const opened: Promise<void> = this.#bonus.open(reels);
    const charged: Promise<void> = this.#gauges.charge(played.charged);

    // Заряд слышно один раз на спин, а не по разу на манометр: стрелки идут разом, и три
    // одинаковых звука вместе слились бы в один громкий.
    if (played.charged.length > 0)
      sfx('multiplierUp');

    // Заряд манометры принимают разом, а стрелки его только доигрывают, поэтому сумма
    // для счётчика готова уже здесь. Перекидывается он вместе со стрелками: ход один.
    const counted: Promise<void> = this.#total.set(totalValue(this.#gauges.values));

    await Promise.all([opened, charged, counted]);

    // Оборванный показ хода не играет: игрок его торопит, а не смотрит.
    const values: number[] = multiplierValues(this.#gauges.values);

    if (this.#rushed)
      this.#bonus.showMultipliers(values);
    else
      this.#bonus.playMultipliers(values);
  }

  /**
   * Показывает, чем кончился раунд: играет бонус и объявляет смену стадии.
   *
   * Створки и манометры здесь уже на месте: их двигал каждый спин своим ходом. Раунду
   * остаётся то, что меняется раз за раунд.
   *
   * Бонус идёт первым и сам возвращает фичу в стадию 1, поэтому смену стадии после него
   * объявлять нечего: игрок только что видел, чем кончился бонус.
   *
   * Объявление стадии закрывает поле заглушкой с её именем и снова открывает его, как перед
   * nudge respin. Под конец фича сверяется с состоянием после раунда: оборванный скипом показ
   * мог не дойти до своего конца, и здесь всё встаёт на места без хода.
   */
  async afterRound(round: RoundResult): Promise<void> {
    this.#rushed = false;

    const after: SteamSpinState | undefined = round.after?.steamSpin;
    if (after === undefined)
      return;

    const stage: number = this.#state.stage;

    this.#state = after;

    if (round.bonus !== undefined) {
      await this.#playBonus(round.bonus, after);
      return;
    }

    if (after.stage !== stage)
      await this.#cover.announce(text('steamCharging'));

    this.#show(after);
  }

  /**
   * Проходит показ фичи разом.
   *
   * Каждая часть укорачивает то, что идёт сейчас, а флаг не даёт начаться следующим шагам
   * бонуса. Свернуться бонус обязан сам: раунд, который его оборвал, стоит и ждёт его конца.
   */
  skip(): void {
    this.#rushed = true;

    this.#camera.skip();
    this.#bonus.skip();
    this.#gauges.skip();
    this.#total.skip();
  }

  update(seconds: number): void {
    this.#bonus.update(seconds);
    this.#total.update(seconds);
    this.#multipliers.update();
  }

  /**
   * Играет бонус: спин мини-слота, его выигрыш и поздравление, см. GDD, разделы 5.3, 5.4 и 6.
   *
   * Поле стоит закрытым весь бонус: сначала на нём объявление, потом счётчик выигрыша, потом
   * поздравление. Мини-слот лежит выше поля, и на время его спина взгляд подходит к нему ближе.
   *
   * Ход у обоих запусков один. Red Hot Spin меняет в нём две вещи: надпись на закрытом поле
   * и перегрев, который идёт после кнопки, см. GDD, раздел 6.2. Steam Spin шаг перегрева
   * проходит пустым: манометры и окна мини-слота у него уже на месте.
   *
   * Оборванный показ дальше не идёт: шаги перестают начинаться, а сброс фичи проходит разом.
   * Свернуться бонус обязан до конца: раунд, который его оборвал, ждёт этой строки, и поле,
   * оставшееся закрытым, закрыло бы следующий раунд целиком.
   *
   * @param after Состояние фичи после бонуса. Бонус сбрасывает её, поэтому это начало игры.
   */
  async #playBonus(bonus: BonusSpin, after: SteamSpinState): Promise<void> {
    // Бонус идёт под свою музыку и объявляется своим звуком: игра ушла с поля, и ухо слышит
    // это раньше, чем глаз дочитает надпись на створках.
    sfx('bonusStart');
    music('freeSpins');

    // Бонус открывается тем же паром, что и створка мини-слота, а под манометрами пар стоит
    // всё это время: машина работает на полную, см. ui/steam.ts. Снимает завесу конец бонуса.
    this.#steam.burst();
    this.#steam.hold();

    // Поезд трогается со станции и идёт весь бонус, см. ADR-51. Ждать его разгон нечего:
    // он идёт своим чередом, пока створки объявляют бонус и взгляд подходит к мини-слоту.
    this.#ride.depart();

    const steps: (() => Promise<void>)[] = [
      () => this.#cover.hold(bonusCaption(bonus.trigger)),
      () => this.#cover.press(text('spinButton'), SPIN_BUTTON_SECONDS),

      () => this.#overload(bonus),

      // Взгляд подходит к мини-слоту, а множители в его окнах ещё стоят: игра переключается
      // на барабаны 4-6 с уже набранными множителями, см. GDD, разделы 5.3 и 6.2.
      () => this.#camera.moveTo(REELS_GRID.bonus.centerY, BONUS_ZOOM),

      // Множители уходят, и под ними крутятся барабаны бонуса.
      () => this.#bonus.spin(bonus.spin),
      () => this.#bonus.showWin(bonus.spin),
      () => this.#camera.moveHome(),

      () => {
        // Поезд подходит к станции, пока на закрытом поле бежит счётчик выигрыша: тормозит
        // он дольше, чем игрок читает поздравление, и ждать его в конце было бы простоем.
        void this.#ride.arrive();
        return this.#cover.count(bonus.payout);
      },
      () => {
        sfx('bonusEnd');
        this.#cover.say(`${congratulation()}\n${moneyText(bonus.payout)}`);
        return this.#cover.press(text('okButton'));
      },
    ];

    for (const step of steps) {
      if (this.#rushed)
        break;

      await step();
    }

    await this.#reset(after);

    // Бонус кончился вместе со своей музыкой: дальше игра идёт под обычную. Оборванный показ
    // проходит те же строки, поэтому музыка возвращается и с него.
    music('main');
  }

  /**
   * Ведёт манометры и мини-слот к тому, с чем бонус крутится: стрелки на своём заряде,
   * а в окнах их множители.
   *
   * Перегрев Red Hot Spin ставит все стрелки на потолок и открывает окна: в них встают три
   * десятки, см. GDD, раздел 6.2. Идёт это до подхода взгляда, а не после: игрок должен
   * увидеть, на что множится выигрыш, до того как барабаны тронутся. Открытые окна открытыми
   * и остаются — дальше под теми же створками крутится бонус.
   *
   * Steam Spin этот шаг проходит пустым: его манометры зарядили спины раунда, окна открыты
   * с той поры, и множители в них уже стоят.
   *
   * Манометры и мини-слот лежат выше поля, поэтому закрытое поле их не прячет.
   */
  #overload(bonus: BonusSpin): Promise<void> {
    // Окна читаются до хода створок: открытые дальше не различить от тех, что открыл перегрев.
    const closed: number[] = this.#bonus.closed;

    const charged: Promise<void> = this.#gauges.charge(bonus.charged);
    const counted: Promise<void> = this.#total.set(bonus.multiplier);

    // Заряд манометры приняли разом, а стрелки его только доигрывают, поэтому значения
    // для окон готовы уже здесь. Окно под закрытой створкой играет своё число не сейчас,
    // а вместе с ней: створка уезжает, и число проступает следом.
    this.#bonus.playMultipliers(multiplierValues(this.#gauges.values));

    const opened: Promise<void> = this.#bonus.open(closed);

    return Promise.all([charged, counted, opened]).then(() => undefined);
  }

  /**
   * Возвращает игру к началу: взгляд на место, стрелки в ноль, створки мини-слота закрыты,
   * поле открыто.
   *
   * Взгляд возвращается и здесь, а не только своим шагом: оборванный посреди бонуса показ
   * оставил бы игру приближенной к мини-слоту. Дойдя сюда своим ходом, взгляд уже дома,
   * и второй ход выходит пустым.
   *
   * Поезд встаёт у станции здесь же, а не только своим шагом: оборванный посреди бонуса показ
   * оставил бы его на полном ходу. Дойдя сюда своим ходом, поезд уже тормозит, и второго
   * прибытия не выходит.
   *
   * Оборванный показ этих ходов не ждёт: игрок уже начал следующий раунд, и тот стоит
   * на этой строке. Само положение игра принимает в любом случае — с ходом или без него.
   * Фон при этом дотормаживает сам: обрывать поездку на полуслове нечем, см. ADR-50.
   */
  async #reset(after: SteamSpinState): Promise<void> {
    // Бонус кончился, и пар под манометрами редеет. Идёт это и на оборванном показе: завеса,
    // оставленная стоять, тянулась бы через весь следующий раунд.
    this.#steam.release();

    const arrived: Promise<void> = this.#ride.arrive();
    const back: Promise<void> = Promise.all([this.#camera.moveHome(), this.#gauges.reset(), this.#total.set(0), this.#bonus.close()]).then(() => undefined);

    if (this.#rushed)
      this.#camera.skip();
    else
      await Promise.all([back, arrived]);

    this.#show(after);

    const released: Promise<void> = this.#cover.release();

    if (!this.#rushed)
      await released;
  }

  /** Ставит фичу в заданное состояние без хода: манометры, счётчик, множители и створки. */
  #show(state: SteamSpinState): void {
    this.#gauges.show(state.gauges);
    this.#total.show(totalValue(state.gauges));
    this.#bonus.showMultipliers(multiplierValues(state.gauges));
    this.#bonus.show(state.opened);
  }
}

/** Заводит фичу Steam Spin, если она есть и в описании игры, и в состоянии сессии. */
export function createSteamSpin(parts: Omit<SteamSpinParts, 'state'> & { readonly session: SessionState }): SteamSpin | null {
  if (parts.config.steamSpin === undefined || parts.session.steamSpin === undefined)
    return null;

  return new SteamSpin({ ...parts, state: parts.session.steamSpin });
}
